import type { Todo } from "../../domain/entities.ts";
import { bindMyDayPicker, myDayPickerView, type MyDayPickerOptions } from "./my-day-controller.ts";
import { carryForwardIds, moveMyDayTask, myDayTasks, planFor, updatePlan, type MyDayPlan, type MyDayPlans } from "./my-day.ts";

export interface MyDayWorkflowCapabilities {
  document: Document;
  plans(): MyDayPlans;
  setPlans(plans: MyDayPlans): void;
  tasks(): Todo[];
  userId(): string;
  today(): string;
  persist(plans: MyDayPlans): Promise<void>;
  context(task: Todo): string;
  icon(name: string): string;
  showMyDay(): Promise<boolean>;
  render(): Promise<void>;
  undo(message: string, restore: () => Promise<void>): void;
  toast(message: string, error?: boolean): void;
}

export function myDaySummaryView(plan: MyDayPlan, tasks: Todo[], carryIds: string[]): string {
  const chosen = myDayTasks(plan, tasks);
  const completed = chosen.filter(task => task.completed).length;
  const carried = carryIds.filter(id => !plan.taskIds.includes(id)).length;
  return `<aside class="my-day-summary"><div><strong role="status">${chosen.length ? `${chosen.length} chosen · ${completed} completed${completed === chosen.length ? " · Today's list is complete" : ""}` : "Nothing chosen for today"}</strong><span>${carried ? `${carried} unfinished from your previous plan. ` : ""}A personal focus list, not a schedule.</span></div><button type="button" class="text-button" data-open-daily-note>Daily note</button></aside>`;
}

export function createMyDayWorkflowController(capabilities: MyDayWorkflowCapabilities) {
  let pickerOpen = false;
  let query = "";
  let limit = 12;
  let unfinishedOpen = false;
  let saving = false;
  let pending: Promise<unknown> = Promise.resolve();
  const currentPlan = (): MyDayPlan => planFor(capabilities.plans(), capabilities.today());
  const savePlan = (update: (plan: MyDayPlan) => MyDayPlan, date = capabilities.today()): Promise<MyDayPlan> => {
    const action = pending.then(async () => {
      const before = planFor(capabilities.plans(), date);
      const plans = updatePlan(capabilities.plans(), update(before));
      await capabilities.persist(plans);
      capabilities.setPlans(plans);
      return before;
    });
    pending = action.catch(() => undefined);
    return action;
  };
  const carryIds = (): string[] => carryForwardIds(capabilities.plans(), capabilities.today(), capabilities.tasks());
  const options = (): MyDayPickerOptions => ({ plan: currentPlan(), tasks: capabilities.tasks(), carryIds: carryIds(), userId: capabilities.userId(), query, limit, unfinishedOpen, context: capabilities.context, icon: capabilities.icon });
  const focusSearch = () => capabilities.document.querySelector<HTMLInputElement>("#my-day-search")?.focus({ preventScroll: true });
  const addAll = async (): Promise<void> => {
    await savePlan(plan => ({ ...plan, taskIds: [...plan.taskIds, ...carryIds()] }));
    await capabilities.render();
    focusSearch();
  };
  const leave = async (id: string): Promise<void> => {
    if (!currentPlan().taskIds.includes(id)) return;
    const before = await savePlan(plan => ({ ...plan, taskIds: plan.taskIds.filter(taskId => taskId !== id) }));
    const index = before.taskIds.indexOf(id);
    await capabilities.render();
    capabilities.undo("Removed from My Day. The task is unchanged.", async () => {
      // Restore only this membership, preserving later choices and the original day's plan.
      if (!capabilities.tasks().some(task => task.id === id && !task.deleted && !task.archivedAt)) return;
      await savePlan(plan => {
        if (plan.taskIds.includes(id)) return plan;
        const ids = [...plan.taskIds]; ids.splice(Math.min(index, ids.length), 0, id);
        return { ...plan, taskIds: ids };
      }, before.date);
      await capabilities.render();
    });
  };

  return {
    currentPlan,
    tasks: (tasks = capabilities.tasks()) => myDayTasks(currentPlan(), tasks),
    summary: () => myDaySummaryView(currentPlan(), capabilities.tasks(), carryIds()),
    picker: () => pickerOpen ? myDayPickerView(options()) : "",
    async open(): Promise<void> {
      pickerOpen = true;
      if (await capabilities.showMyDay()) {
        capabilities.document.querySelector<HTMLElement>("[data-my-day-picker]")?.scrollIntoView({ block: "nearest" });
        focusSearch();
      }
    },
    bind(root: ParentNode): void {
      bindMyDayPicker(root, {
        options,
        search: value => { query = value; limit = 12; }, more: () => { limit += 24; }, unfinished: value => { unfinishedOpen = value; },
        close: async () => { pickerOpen = false; await capabilities.render(); capabilities.document.querySelector<HTMLElement>('[data-action="plan-my-day"]')?.focus(); },
        addAll: async () => { if (saving) return; saving = true; try { await addAll(); } finally { saving = false; } },
        toggle: async id => {
          if (saving) return;
          const task = capabilities.tasks().find(item => item.id === id && !item.deleted && !item.archivedAt && !item.completed);
          if (!task) return;
          saving = true;
          try {
            const plan = currentPlan();
            if (plan.taskIds.includes(id)) await leave(id);
            else { await savePlan(latest => ({ ...latest, taskIds: [...latest.taskIds, id] })); await capabilities.render(); }
            const button = Array.from(root.querySelectorAll<HTMLElement>("[data-my-day-toggle]")).find(item => item.dataset.myDayToggle === id);
            if (button) button.focus({ preventScroll: true }); else focusSearch();
          } finally { saving = false; }
        },
      });
    },
    carryForward: addAll,
    async leave(id: string): Promise<void> {
      try { await leave(id); } catch { capabilities.toast("Could not save your My Day selection on this device. Please try again.", true); }
    },
    async move(id: string, direction: -1 | 1): Promise<void> { await savePlan(plan => moveMyDayTask(plan, id, direction)); },
    async reorder(ids: string[]): Promise<void> { await savePlan(plan => ({ ...plan, taskIds: [...ids.filter(id => plan.taskIds.includes(id)), ...plan.taskIds.filter(id => !ids.includes(id))] })); },
  };
}
